import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface UIState {
  sidebarOpen: boolean;
  hideSidebar: boolean;
  theme: 'light' | 'dark' | 'system';
  isMobile: boolean;
  activeTab: string;
  modals: {
    createProject: boolean;
    createTask: boolean;
    settings: boolean;
    deleteConfirm: boolean;
    projectSettings: boolean;
    chatHistory: boolean;
  };
  deleteConfirmData: {
    title: string;
    message: string;
    onConfirm: () => void;
  } | null;
}

interface UIActions {
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setHideSidebar: (hide: boolean) => void;
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  setIsMobile: (isMobile: boolean) => void;
  setActiveTab: (tab: string) => void;
  openModal: (modal: keyof UIState['modals']) => void;
  closeModal: (modal: keyof UIState['modals']) => void;
  openDeleteConfirm: (data: UIState['deleteConfirmData']) => void;
  closeDeleteConfirm: () => void;
}

type UIStore = UIState & UIActions;

const applyTheme = (theme: 'light' | 'dark' | 'system') => {
  if (theme === 'dark') {
    document.documentElement.classList.add('dark');
  } else if (theme === 'light') {
    document.documentElement.classList.remove('dark');
  } else {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (prefersDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }
};

export const useUIStore = create<UIStore>()(
  persist(
    (set) => ({
      sidebarOpen: true,
      hideSidebar: false,
      theme: 'system',
      isMobile: false,
      activeTab: 'tasks',
      modals: {
        createProject: false,
        createTask: false,
        settings: false,
        deleteConfirm: false,
        projectSettings: false,
        chatHistory: false,
      },
      deleteConfirmData: null,

      toggleSidebar: () =>
        set((state) => ({
          sidebarOpen: !state.sidebarOpen,
        })),

      setSidebarOpen: (open) =>
        set({
          sidebarOpen: open,
        }),

      setHideSidebar: (hide) =>
        set({
          hideSidebar: hide,
        }),

      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },

      setIsMobile: (isMobile) =>
        set({
          isMobile,
          sidebarOpen: !isMobile,
        }),

      setActiveTab: (tab) =>
        set({
          activeTab: tab,
        }),

      openModal: (modal) =>
        set((state) => ({
          modals: { ...state.modals, [modal]: true },
        })),

      closeModal: (modal) =>
        set((state) => ({
          modals: { ...state.modals, [modal]: false },
        })),

      openDeleteConfirm: (data) =>
        set((state) => ({
          modals: { ...state.modals, deleteConfirm: true },
          deleteConfirmData: data,
        })),

      closeDeleteConfirm: () =>
        set((state) => ({
          modals: { ...state.modals, deleteConfirm: false },
          deleteConfirmData: null,
        })),
    }),
    {
      name: 'ui-storage',
      partialize: (state) => ({ theme: state.theme }),
      onRehydrateStorage: () => (state) => {
        if (state?.theme) {
          applyTheme(state.theme);
        }
      },
    }
  )
);
